<script setup lang="ts">
import { useFetch, useRuntimeConfig, useState } from '#app'
import type { Flat } from '~/types/types'
import { computed, ref, watchEffect, onMounted } from 'vue'
import type { CmsCustomComponentType } from '~/layers/cms/app/types/cmsTypes'

import garaz from '~/assets/images/ghosty/Garaz.png'
import parter from '~/assets/images/ghosty/Parter.png'
import p1 from '~/assets/images/ghosty/Rzut_1.png'
import p2 from '~/assets/images/ghosty/Rzut_2.png'
import p3 from '~/assets/images/ghosty/Rzut_3.png'
import p4 from '~/assets/images/ghosty/Rzut_4.png'
import GhostHoverPanel from '~/components/GhostHoverPanel.vue'
import GhostParter from '~/components/ghostyNajem/GhostParter.vue'
import GhostP1 from '~/components/ghostyNajem/GhostP1.vue'
import GhostP2 from '~/components/ghostyNajem/GhostP2.vue'
import GhostP3 from '~/components/ghostyNajem/GhostP3.vue'
import GhostP4 from '~/components/ghostyNajem/GhostP4.vue'
import { getLokaleWithHistoryNajem } from '#imports'

const Flats = useState<Record<number, Flat>>('Flats')

const config = useRuntimeConfig()
const { data } = await useFetch(`${config.public.apiBase}/api/get-apartamenty-with-price-change-history-najem`, {
  transform: response => (response ?? []) as Flat[],
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})

watchEffect(() => {
  if (!data.value) return
  Flats.value = Object.fromEntries(data.value.map(f => [f.id, f]))
})

onMounted(async () => {
  const res = await getLokaleWithHistoryNajem()
  if (res.status_ok) {
    data.value = res.data as Flat[]
    Flats.value = Object.fromEntries((res.data as Flat[]).map(f => [f.id, f]))
  }
})

const props = defineProps<{
  cmsDataPath: (string | number)[]
}>()
const nazwa_komponentu = 'Special/GhostyNajem'
const component_default_values: CmsCustomComponentType = {
  id: { value: undefined, default_value: 'Lorem Ipsum', settingType: 'text', setting_label: 'zmien id' }
}

const floors = [
  { image: garaz, ghost: null },
  { image: parter, ghost: GhostParter },
  { image: p1, ghost: GhostP1 },
  { image: p2, ghost: GhostP2 },
  { image: p3, ghost: GhostP3 },
  { image: p4, ghost: GhostP4 }
]

const choosenFloorIndex = ref(0)
const choosenFloor = computed(() => floors[choosenFloorIndex.value])

const chooseFloor = (index: number) => {
  if (index < 0 || index >= floors.length) return
  choosenFloorIndex.value = index
}
</script>

<template>
  <CmsCustomComponent
    v-slot="{ component_values }"
    :cms-data-path="[...props.cmsDataPath]"
    :component-name="nazwa_komponentu"
    :defaults="component_default_values"
    :open-options="true"
  >
    <div
      :id="component_values?.id?.value"
      class="py-20 flex flex-col items-center gap-15 relative"
    >
      <div class="flex flex-row gap-5 px-2xl overflow-x-auto max-w-full scrollbar-thin py-1">
        <div
          class="rounded-full flex items-center py-5 px-7.5 hover:bg-Primary hover:text-white transition-all duration-300 cursor-pointer"
          :class="choosenFloorIndex == 0 ? 'bg-Primary text-white' : 'outline outline-Tekst'"
          @click="chooseFloor(0)"
        >
          <span class="text-Button text-nowrap">Pietro -1</span>
        </div>
        <div
          class="rounded-full flex items-center py-5 px-7.5 hover:bg-Primary hover:text-white transition-all duration-300 cursor-pointer"
          :class="choosenFloorIndex == 1 ? 'bg-Primary text-white' : 'outline outline-Tekst'"
          @click="chooseFloor(1)"
        >
          <span class="text-Button text-nowrap">Parter</span>
        </div>
        <div
          class="rounded-full flex items-center py-5 px-7.5 hover:bg-Primary hover:text-white transition-all duration-300 cursor-pointer"
          :class="choosenFloorIndex == 2 ? 'bg-Primary text-white' : 'outline outline-Tekst'"
          @click="chooseFloor(2)"
        >
          <span class="text-Button text-nowrap">Pietro 1</span>
        </div>
        <div
          class="rounded-full flex items-center py-5 px-7.5 hover:bg-Primary hover:text-white transition-all duration-300 cursor-pointer"
          :class="choosenFloorIndex == 3 ? 'bg-Primary text-white' : 'outline outline-Tekst'"
          @click="chooseFloor(3)"
        >
          <span class="text-Button text-nowrap">Pietro 2</span>
        </div>
        <div
          class="rounded-full flex items-center py-5 px-7.5 hover:bg-Primary hover:text-white transition-all duration-300 cursor-pointer"
          :class="choosenFloorIndex == 4 ? 'bg-Primary text-white' : 'outline outline-Tekst'"
          @click="chooseFloor(4)"
        >
          <span class="text-Button text-nowrap">Pietro 3</span>
        </div>
        <div
          class="rounded-full flex items-center py-5 px-7.5 hover:bg-Primary hover:text-white transition-all duration-300 cursor-pointer"
          :class="choosenFloorIndex == 5 ? 'bg-Primary text-white' : 'outline outline-Tekst'"
          @click="chooseFloor(5)"
        >
          <span class="text-Button text-nowrap">Pietro 4</span>
        </div>
      </div>
      <div class="px-2xl flex flex-col items-center gap-15 relative">
        <div class="w-full grid grid-cols-1 grid-rows-1 ">
          <div class="col-start-1 col-end-1 row-start-1 row-end-1 relative z-10">
            <Teleport to="body">
              <GhostHoverPanel />
            </Teleport>
            <component :is="choosenFloor?.ghost" />
          </div>
          <div class="col-start-1 col-end-1 row-start-1 row-end-1 relative z-20 pointer-events-none">
            <img
              :src="choosenFloor?.image"
              loading="lazy"
              alt="bg"
              width="1580"
              height="824"
            >
          </div>
        </div>
        <div />
        <div class=" flex flex-col xl:flex-row gap-4 xl:gap-15">
          <div class="flex flex-row items-center gap-2.5">
            <div class="border border-Tekst bg-Wolne w-5 h-5 rounded-full" />
            <span class="text-Menu text-Tekst">
              Wolne
            </span>
          </div>
          <div class="flex flex-row items-center gap-2.5">
            <div class="border border-Tekst bg-Rezerwacja w-5 h-5 rounded-full" />
            <span class="text-Menu text-Tekst">
              Rezerwacja
            </span>
          </div>
          <div class="flex flex-row items-center gap-2.5">
            <div class="border border-Tekst bg-Sprzedane w-5 h-5 rounded-full" />
            <span class="text-Menu text-Tekst">
              Sprzedane
            </span>
          </div>
          <div class="flex flex-row items-center gap-2.5">
            <div class="border border-Tekst bg-Wynajete w-5 h-5 rounded-full" />
            <span class="text-Menu text-Tekst">
              Wynajęte
            </span>
          </div>
        </div>
      </div>
    </div>
  </CmsCustomComponent>
</template>
